/**
 * The admin user list: what is loaded, what is filtered, what is selected.
 *
 * Lifted out of user-management, which held seven pieces of state and four
 * components in one file. Six of those states answer one question -- which
 * users are on screen and which of them are ticked -- and they interlock:
 * changing the filter resets the page and the ticks, a refetch drops the ticks
 * because the rows behind them may be gone, and deleting reads the tick set.
 *
 * What stays in the component is which form is open, because that is the view
 * and not the list.
 */
import { loadFailure, deleteFailure, type FormFailure } from './save-failure-words';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAdminUsers, useDeleteUser } from '../../api/admin/users/hooks';
import type { UserListItem } from '../../api/admin/users/types';
import { confirmDialog } from '../../components/confirm-dialog/confirm-dialog-store';

export type UserTypeFilter = '' | 'STUDENT' | 'MODERATOR' | 'PONDER_ADMIN';

const PAGE_SIZE = 20;

export function useUserList() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<UserTypeFilter>('');
  const [searchText, setSearchText] = useState('');
  const [pageError, setPageError] = useState<FormFailure | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const usersQuery = useAdminUsers(page, PAGE_SIZE, typeFilter || undefined);
  const deleteUser = useDeleteUser();
  const users: UserListItem[] = usersQuery.data?.data ?? [];
  const total = usersQuery.data?.total ?? 0;
  const totalPages = usersQuery.data?.totalPages ?? 1;
  const isLoading = usersQuery.isLoading;
  const queryError = usersQuery.isError ? loadFailure(t, usersQuery.error, 'users') : null;
  const error = pageError ?? queryError;
  const isDeleting = deleteUser.isPending;

  // Adjust during render rather than in an effect.
  //
  // Both of these react to a value changing, which is what React documents this
  // pattern for: comparing against the previous value and setting state during
  // render lets React discard the in-progress output and re-render immediately,
  // where an effect renders once with stale state and then again to fix it.
  const [prevFilter, setPrevFilter] = useState(typeFilter);
  if (prevFilter !== typeFilter) {
    setPrevFilter(typeFilter);
    setPage(1);
    setSelectedIds(new Set());
  }

  // The list refreshed: whatever was selected may no longer be on it.
  const [prevUpdatedAt, setPrevUpdatedAt] = useState(usersQuery.dataUpdatedAt);
  if (prevUpdatedAt !== usersQuery.dataUpdatedAt) {
    setPrevUpdatedAt(usersQuery.dataUpdatedAt);
    setSelectedIds(new Set());
  }

  const reload = () => { void usersQuery.refetch(); };

  const q = searchText.toLowerCase();
  const filteredUsers = q
    ? users.filter(
        (u) =>
          u.firstName.toLowerCase().includes(q) ||
          u.lastName.toLowerCase().includes(q) ||
          u.name.toLowerCase().includes(q) ||
          (u.email ?? '').toLowerCase().includes(q) ||
          (u.username ?? '').toLowerCase().includes(q),
      )
    : users;

  const allSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selectedIds.has(u.id));
  const someSelected = selectedIds.size > 0;

  const handleDeleteSingle = async (user: UserListItem) => {
    if (!(await confirmDialog({ message: t('admin.users.deleteConfirm', { name: `${user.firstName} ${user.lastName}` }), tone: 'danger' }))) return;
    setPageError(null);
    try {
      await deleteUser.mutateAsync(user.id);
    } catch (e) {
      setPageError(deleteFailure(t, e, 'user'));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!(await confirmDialog({ message: t('admin.users.deleteSelectedConfirm', { count: selectedIds.size }), tone: 'danger' }))) return;
    setPageError(null);
    try {
      await Promise.all([...selectedIds].map((id) => deleteUser.mutateAsync(id)));
    } catch (e) {
      setPageError(deleteFailure(t, e, 'user'));
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredUsers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredUsers.map((u) => u.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return {
    page,
    setPage,
    typeFilter,
    setTypeFilter,
    searchText,
    setSearchText,
    users,
    filteredUsers,
    total,
    totalPages,
    isLoading,
    isDeleting,
    error,
    selectedIds,
    allSelected,
    someSelected,
    reload,
    handleDeleteSingle,
    handleDeleteSelected,
    toggleSelectAll,
    toggleSelect,
  };
}
